import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { showBullPhishOrgSelect } from '@app/store/actions';
import { CopyComponent, PasslySwitch } from '@app/components';
import { mainMenuTypes, IMenuItem } from '@app/components/Sidebar/menuObject';
import { getMenuItemById } from '@app/helpers';
import { agentsService } from '@app/services';
import { AxiosError, AxiosResponse } from 'axios';

interface IBullphishAgent {
    id: string;
    clientId: string;
    secret: string;
    status: boolean;
}

const BullphishPage = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const currentOrgId = useSelector((state: any) => state.listPageBar.bullPhishSelectedOrganization);
    const [agent, setAgent] = useState<IBullphishAgent | null>(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isShowSecret, setIsShowSecret] = useState(false);
    const [isSwitchEnabled, setIsSwitchEnabled] = useState(false);

    const item: IMenuItem | undefined = getMenuItemById(mainMenuTypes.Bullphish);
    useSetPage(null, item);

    useEffect(() => {
        dispatch(showBullPhishOrgSelect());
    }, []);

    useEffect(() => {
        if (!currentOrgId) {
            return;
        }

        setIsShowSecret(false);
        getPublicAgent(currentOrgId);
    }, [currentOrgId]);

    const triggerIntegrationState = () => {
        setIsSwitchEnabled(false);
        if (agent === null) {
            agentsService.registerPublicAgent(
                currentOrgId,
                (response: AxiosResponse) => {
                    setAgent(response.data);
                    setErrorMessage('');
                    setIsSwitchEnabled(true);
                },
                (error: AxiosError<any>) => {
                    if (error.response?.status === 409) {
                        updatePublicAgent(currentOrgId, true);
                    } else {
                        setErrorMessage(translation('GENERIC_ERROR'));
                    }
                    setIsSwitchEnabled(true);
                });
        } else {
            updatePublicAgent(currentOrgId, !agent.status);
        }
    };

    const getPublicAgent = (organizationId: string) => {
        agentsService.getPublicAgent(
            organizationId,
            (response: AxiosResponse) => {
                setAgent(response.data);
                setErrorMessage('');
                setIsSwitchEnabled(true);
            },
            (error: AxiosError<any>) => {
                if (error.response?.status === 404) {
                    setAgent(null);
                } else {
                    setErrorMessage(translation('GENERIC_ERROR'));
                }
                setIsSwitchEnabled(true);
            });
    };

    const updatePublicAgent = (organizationId: string, enabled: boolean) => {
        agentsService
            .updatePublicAgent(
                organizationId,
                enabled,
                (_: AxiosResponse) => {
                    setAgent({...agent!, status: enabled});
                    setErrorMessage('');
                    setIsSwitchEnabled(true);
                    if (enabled) {
                        setIsShowSecret(false);
                    }
                },
                (error: AxiosError<any>) => {
                    if (error.response?.status === 404) {
                        getPublicAgent(currentOrgId);
                    } else {
                        setErrorMessage(translation('GENERIC_ERROR'));
                    }
                    setIsSwitchEnabled(true);
                });
    };

    const showSecret = () => {
        setIsShowSecret(true);
    };

    return (
        <>
            <div className='bullphish'>
                <div className='content'>
                    <h1 className='header--h1'>{translation('INTEGRATION_BULLPHISH_TITLE')}</h1>
                    <div className='bullphish__switch-container'>
                        <PasslySwitch
                            checked={agent?.status || false}
                            onChange={triggerIntegrationState}
                            disabled={!isSwitchEnabled}
                            label={translation('INTEGRATION_BULLPHISH_ENABLE_TOGGLE_LABEL')}
                        />
                    </div>
                    {agent !== null && agent.status &&
                    <>
                    <div className='bullphish__text-container'>
                        <span className='bullphish__primary-label'>{translation('AGENT_CLIENT_ID')}</span>
                        <span className='bullphish__secondary-label'>{agent.clientId}</span>
                        <div className='bullphish__copy-icon'>
                            <CopyComponent valueToCopy={agent!.clientId} />
                        </div>
                    </div>
                    <div className='bullphish__text-container'>
                        <span className='bullphish__primary-label'>{translation('AGENT_CLIENT_KEY')}</span>
                        {!isShowSecret ?
                        <span className='bullphish__secondary-label link' onClick={showSecret}>{translation('SHOW_KEY')}</span> :
                        <span className='bullphish__secondary-label'>{agent.secret}</span>}
                        <div className={isShowSecret ? 'bullphish__copy-icon' : 'bullphish__copy-icon hidden'}>
                            <CopyComponent valueToCopy={agent!.secret} />
                        </div>
                    </div>
                    </>}
                    {errorMessage && <div className='bullphish__error'>{errorMessage}</div>}
                </div>
            </div>
        </>
    );
};

export default BullphishPage;